import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../API';
import styles from './Documentation.module.css';

function Documentation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [fileTree, setFileTree] = useState(null);
  const [markdownContent, setMarkdownContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Extract documentation path from URL
  // location.pathname will be like "/documentation" or "/documentation/path/to/file.md"
  const getDocPath = (pathname) => {
    const pathMatch = pathname.match(/^\/documentation\/(.+)$/);
    return pathMatch ? pathMatch[1] : '';
  };
  
  const [selectedPath, setSelectedPath] = useState(() => getDocPath(location.pathname));

  // Fetch file tree on mount
  useEffect(() => {
    const fetchFileTree = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('docs');
        if (response.status === 200 && response.data?.files) {
          setFileTree(response.data.files);
        } else {
          setError('Failed to load documentation index');
        }
      } catch (err) {
        setError('Error loading documentation: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchFileTree();
  }, []);

  // Fetch markdown content when path changes
  useEffect(() => {
    const fetchMarkdown = async () => {
      if (!selectedPath) {
        setMarkdownContent(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await api.get('docs/file', { 
          query: { path: selectedPath },
          responseType: 'text',
          headers: {
            'Accept': 'text/markdown'
          }
        });
        if (response.status === 200) {
          setMarkdownContent(response.data);
        } else {
          setError('Failed to load documentation file');
          setMarkdownContent(null);
        }
      } catch (err) {
        setError('Error loading documentation: ' + (err.message || 'Unknown error'));
        setMarkdownContent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, [selectedPath]);

  // Update selected path when location changes
  useEffect(() => {
    const currentPath = getDocPath(location.pathname);
    if (currentPath !== selectedPath) {
      setSelectedPath(currentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleFileSelect = (path) => {
    setSelectedPath(path);
    navigate(`/documentation/${path}`);
  };

  const renderFileTree = (files, level = 0) => {
    if (!files || files.length === 0) {
      return null;
    }

    return (
      <ul className={level === 0 ? styles.fileTree : styles.fileTreeNested}>
        {files.map((item) => (
          <li key={item.path} className={styles.treeItem}>
            {item.type === 'file' ? (
              <Link
                to={`/documentation/${item.path}`}
                className={`${styles.treeLink} ${selectedPath === item.path ? styles.active : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleFileSelect(item.path);
                }}
              >
                {item.name}
              </Link>
            ) : (
              <>
                <span className={styles.treeDir}>{item.name}</span>
                {item.children && item.children.length > 0 && (
                  <div className={styles.treeChildren}>
                    {renderFileTree(item.children, level + 1)}
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    );
  };

  if (loading && !fileTree) {
    return (
      <div className={styles.documentationWrapper}>
        <div className={styles.documentation}>
          <p>Loading documentation...</p>
        </div>
      </div>
    );
  }

  if (error && !fileTree) {
    return (
      <div className={styles.documentationWrapper}>
        <div className={styles.documentation}>
          <div className={styles.error}>
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.documentationWrapper}>
      <div className={styles.documentation}>
        <div className={styles.sidebar}>
          <h2>Documentation</h2>
          {fileTree && (
            <nav className={styles.nav}>
              {renderFileTree(fileTree)}
            </nav>
          )}
        </div>
        <div className={styles.content}>
          {loading && markdownContent === null ? (
            <p>Loading...</p>
          ) : error && !markdownContent ? (
            <div className={styles.error}>
              <h2>Error</h2>
              <p>{error}</p>
            </div>
          ) : markdownContent ? (
            <div className={styles.markdownContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {markdownContent}
              </ReactMarkdown>
            </div>
          ) : (
            <div className={styles.empty}>
              <h2>Select a documentation file</h2>
              <p>Choose a file from the sidebar to view its contents.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Documentation;
